import type { RouteLocationRaw } from 'vue-router';
import { navigateTo, useRuntimeConfig } from 'nuxt/app';

export type ApplicationCode = 'cam' | 'mar' | 'mobile' | 'admin';

type CamPortalURL
	= 'register'
		| 'forgot-password'
		| 'onboarding'
		| 'onboarding-personal'
		| 'onboarding-business'
		| 'onboarding-personal-full'
		| 'onboarding-personal-complete'
		| 'onboarding-full'
		| 'overview'
		| 'account-info'
		| 'data-and-privacy'
		| 'my-accounts'
		| 'user-details'
		| 'passwords-and-security'
		| 'payment-methods'
		| 'review-personal'
		| 'review-business'
	;

export function useCamPortalURL(link?: CamPortalURL): RouteLocationRaw {
	const { camPortalUrl } = useRuntimeConfig().public;

	const urlMap: Record<CamPortalURL, string> = {
		'register': '/register',
		'forgot-password': '/reset-password',
		'onboarding': '/onboarding',
		'onboarding-personal': '/onboarding/personal',
		'onboarding-business': '/onboarding/business',
		'onboarding-personal-full': '/onboarding/personal?fullOnboarding=true',
		'onboarding-personal-complete': '/onboarding/complete-personal',
		'onboarding-full': '/onboarding?fullOnboarding=true',
		'overview': '/overview',
		'account-info': '/account-info',
		'data-and-privacy': '/data-and-privacy',
		'my-accounts': '/user-settings/my-accounts',
		'user-details': '/user-settings/user-details',
		'passwords-and-security': '/user-settings/passwords-and-security',
		'payment-methods': '/plans-and-payments',
		'review-personal': '/onboarding/review/personal',
		'review-business': '/onboarding/review/business',
	};

	return link && urlMap[link]
		? `${camPortalUrl}${urlMap[link]}`
		: camPortalUrl as RouteLocationRaw;
}

type MarPortalURL
	= 'AS_Home'
		| 'AS_Profile'
		| 'PS_Home'
		| 'PS_Profile'
		| 'art-studio'
		| 'art-studio-subscription'
		| 'print-studio'
		| 'gallery'
		| 'certificate-of-authenticity'
		| 'print-quality-assurance'
		| 'hahnemuehles-excellence-program'
		| 'checkout'
		| 'subscription-checkout'
	;

export function useMarPortalURL(link?: MarPortalURL) {
	const { marPortalUrl } = useRuntimeConfig().public;

	const urlMap: Record<MarPortalURL, string> = {
		'AS_Home': '/art-studio', // REMOVE THIS WHEN DONE
		'AS_Profile': '/art-studio/profile', // REMOVE THIS WHEN DONE
		'PS_Home': '/print-studio', // REMOVE THIS WHEN DONE
		'PS_Profile': '/print-studio/profile', // REMOVE THIS WHEN DONE
		'art-studio': '/art-studio',
		'art-studio-subscription': '/art-studio/subscription',
		'print-studio': '/print-studio',
		'gallery': '/gallery',
		'print-quality-assurance': '/print-quality-assurance',
		'certificate-of-authenticity': '/certificate-of-authenticity',
		'hahnemuehles-excellence-program': '/hahnemuehles-excellence-program',
		'checkout': '/checkout',
		'subscription-checkout': '/subscription',
	};

	return link && urlMap[link]
		? `${marPortalUrl}${urlMap[link]}` as string
		: marPortalUrl as string;
}

export type HelpPortalURL
	= 'my-hahnemuehle'
		| 'what-is-hahnemuehle-id-and-where-do-i-find-it'
		| 'i-forgot-my-password-what-should-i-do'
		| 'i-didnt-receive-an-email-with-a-one-time-code'
		| 'i-dont-have-access-to-the-email-associated-with-my-hahnemuehle-id-and-cant-retrieve-the-one-time-code-you-sent-what-are-my-options'
		| 'my-art-registry'
		| 'how-to-determine-the-price-of-fine-art-prints'
		| 'security-you-can-trust-safeguarding-transactions-on-my-art-registry'
		| 'understanding-digital-certificates-of-authenticity'
		| 'uploading-art-to-mar'
		| 'why-creating-a-buyer-account-on-my-art-registry-is-the-smart-choice-for-all-art-lovers'
		| 'certified-print-studios-elevating-quality-and-value'
		| 'revolutionizing-art-promotion-and-sales-the-story-of-my-art-registry'
		| 'the-art-of-tomorrow-embracing-the-nft-revolution'
		| 'how-to-secure-your-hm-account'
		| 'how-can-i-publish-print-artwork-for-sale'
		| 'how-are-digital-artworks-nfts-created-in-blockchain'
		| 'how-does-reference-help-in-the-cross-sale-of-my-print-or-digital-artwork'
		| 'how-does-enabling-inquiry-help-in-the-cross-sale-for-print-artwork'
		| 'how-to-save-artwork-as-a-draft-and-publish-later'
		| 'metadata-mapping-for-bulk-upload'
		| 'setup-mar-custom-template'
		| 'upload-requirements-for-bulk-upload'
	;

export function useHelpPortalURL(link?: HelpPortalURL) {
	const { helpPortalUrl } = useRuntimeConfig().public;

	const urlMap: Record<HelpPortalURL, string> = {
		'my-hahnemuehle': '/my-hahnemuehle',
		'what-is-hahnemuehle-id-and-where-do-i-find-it': '/my-hahnemuehle/what-is-hahnemuehle-id-and-where-do-i-find-it',
		'i-forgot-my-password-what-should-i-do': '/my-hahnemuehle/i-forgot-my-password-what-should-i-do',
		'i-didnt-receive-an-email-with-a-one-time-code': '/my-hahnemuehle/i-didnt-receive-an-email-with-a-one-time-code',
		'i-dont-have-access-to-the-email-associated-with-my-hahnemuehle-id-and-cant-retrieve-the-one-time-code-you-sent-what-are-my-options': '/my-hahnemuehle/i-dont-have-access-to-the-email-associated-with-my-hahnemuehle-id-and-cant-retrieve-the-one-time-code-you-sent-what-are-my-options',
		'my-art-registry': '/my-art-registry',
		'how-to-determine-the-price-of-fine-art-prints': '/my-art-registry/how-to-determine-the-price-of-fine-art-prints',
		'security-you-can-trust-safeguarding-transactions-on-my-art-registry': '/my-art-registry/security-you-can-trust-safeguarding-transactions-on-my-art-registry',
		'understanding-digital-certificates-of-authenticity': '/my-art-registry/understanding-digital-certificates-of-authenticity',
		'uploading-art-to-mar': '/my-art-registry/uploading-art-to-mar',
		'why-creating-a-buyer-account-on-my-art-registry-is-the-smart-choice-for-all-art-lovers': '/my-art-registry/why-creating-a-buyer-account-on-my-art-registry-is-the-smart-choice-for-all-art-lovers',
		'certified-print-studios-elevating-quality-and-value': '/my-art-registry/certified-print-studios-elevating-quality-and-value',
		'revolutionizing-art-promotion-and-sales-the-story-of-my-art-registry': '/my-art-registry/revolutionizing-art-promotion-and-sales-the-story-of-my-art-registry',
		'the-art-of-tomorrow-embracing-the-nft-revolution': '/my-art-registry/the-art-of-tomorrow-embracing-the-nft-revolution',
		'how-to-secure-your-hm-account': '/my-art-registry/how-to-secure-your-hm-account',
		'how-can-i-publish-print-artwork-for-sale': '/my-art-registry/how-can-i-publish-print-artwork-for-sale',
		'how-are-digital-artworks-nfts-created-in-blockchain': '/my-art-registry/how-are-digital-artworks-nfts-created-in-blockchain',
		'how-does-reference-help-in-the-cross-sale-of-my-print-or-digital-artwork': '/my-art-registry/how-does-reference-help-in-the-cross-sale-of-my-print-or-digital-artwork',
		'how-does-enabling-inquiry-help-in-the-cross-sale-for-print-artwork': '/my-art-registry/how-does-enabling-inquiry-help-in-the-cross-sale-for-print-artwork',
		'how-to-save-artwork-as-a-draft-and-publish-later': '/my-art-registry/how-to-save-artwork-as-a-draft-and-publish-later',
		'metadata-mapping-for-bulk-upload': '/my-art-registry/metadata-mapping-for-bulk-upload',
		'setup-mar-custom-template': '/my-art-registry/setup-mar-custom-template',
		'upload-requirements-for-bulk-upload': '/my-art-registry/upload-requirements-for-bulk-upload',
	};

	return link && urlMap[link]
		? `${helpPortalUrl}${urlMap[link]}`
		: helpPortalUrl;
}

export type LegalPortalURL
	= 'privacy-policy'
		| 'general-terms-and-conditions'
		| 'marketplace-terms-of-use'
		| 'cookie-notice'
		| 'my-art-registry-terms-of-use'
		| 'fine-art-print-production-agreement'
		| 'data-storage-and-sellers-agreement'
		| 'print-service-provider-agreement'
		| 'mobile-app-terms-of-use'
		| 'mobile-app-analytics-data-agreement'
	;

export function useLegalPortalURL(link?: LegalPortalURL) {
	const { legalPortalUrl } = useRuntimeConfig().public;

	const urlMap: Record<LegalPortalURL, string> = {
		'privacy-policy': '/privacy-policy',
		'general-terms-and-conditions': '/general-terms-and-conditions',
		'marketplace-terms-of-use': '/marketplace-terms-of-use',
		'cookie-notice': '/cookie-notice',
		'my-art-registry-terms-of-use': '/my-art-registry-terms-of-use',
		'fine-art-print-production-agreement': '/fine-art-print-production-agreement',
		'data-storage-and-sellers-agreement': '/data-storage-and-sellers-agreement',
		'print-service-provider-agreement': '/print-service-provider-agreement',
		'mobile-app-terms-of-use': '/mobile-app-terms-of-use',
		'mobile-app-analytics-data-agreement': '/mobile-app-analytics-data-agreement',
	};

	return link && urlMap[link]
		? `${legalPortalUrl}${urlMap[link]}`
		: legalPortalUrl;
}

export function useAuthPortalURL(type: 'login' | 'logout'): string {
	const { authPortalUrl } = useRuntimeConfig().public;

	const urlMap: Record<'login' | 'logout', string> = {
		login: '/',
		logout: '/logout',
	};

	return urlMap[type] ? `${authPortalUrl}${urlMap[type]}` : authPortalUrl as string;
}

export function useAdminPortalURL(): string {
	const { adminPortalUrl } = useRuntimeConfig().public;

	return adminPortalUrl as string;
}

function sanitizeCallbackUrl(callbackUrl: string): string {
	try {
		const hasScheme = /^[a-z][a-z\d+.-]*:/i.test(callbackUrl);
		const parsed = hasScheme
			? new URL(callbackUrl)
			: new URL(callbackUrl, 'http://localhost');

		parsed.searchParams.delete('code');

		if (hasScheme) {
			return parsed.toString();
		}

		return `${parsed.pathname}${parsed.search}${parsed.hash}`;
	}
	catch {
		return callbackUrl;
	}
}

interface LoginUrlBuilderOptions {
	accountCode?: string;
	callbackUrl?: string;
	loginErrorType?: string;
	loginErrorMessage?: string;
	loginRequired?: boolean;
}

export function useLoginUrlBuilder(applicationCode: ApplicationCode, {
	accountCode,
	callbackUrl,
	loginErrorType,
	loginErrorMessage,
	loginRequired,
}: LoginUrlBuilderOptions = {}) {
	const loginUrl = useAuthPortalURL('login');

	const url = new URL(loginUrl);
	// clear all search params
	url.search = '';

	if (applicationCode) {
		url.searchParams.set('applicationCode', applicationCode);
	}

	if (accountCode) {
		url.searchParams.set('accountCode', accountCode);
	}

	if (callbackUrl) {
		url.searchParams.set('callbackUrl', sanitizeCallbackUrl(callbackUrl));
	}

	if (loginErrorType) {
		url.searchParams.set('loginErrorType', loginErrorType);
	}

	if (loginErrorMessage) {
		url.searchParams.set('loginErrorMessage', loginErrorMessage);
	}

	if (loginRequired !== undefined) {
		url.searchParams.set('loginRequired', loginRequired ? 'true' : 'false');
	}

	return {
		set: (key: keyof LoginUrlBuilderOptions, value: string | boolean) => {
			url.searchParams.delete(key);
			if (key === 'callbackUrl' && typeof value === 'string') {
				url.searchParams.set(key, sanitizeCallbackUrl(value));
				return;
			}

			url.searchParams.set(key, value.toString());
		},
		delete: (key: keyof LoginUrlBuilderOptions) => {
			url.searchParams.delete(key);
		},
		toString: () => url.toString(),
		navigate: async () => {
			await navigateTo(url.toString(), { external: true });
		},
	};
}

export interface LogoutUrlOptions {
	callbackUrl?: string;
	loginCallbackUrl?: string;
	applicationCode?: ApplicationCode;
}

export function useLogoutUrlBuilder(applicationCode?: ApplicationCode, { callbackUrl, loginCallbackUrl }: LogoutUrlOptions = {}) {
	const url = useAuthPortalURL('logout');

	const logoutUrl = new URL(url);
	// clear all search params
	logoutUrl.search = '';

	if (applicationCode) {
		logoutUrl.searchParams.set('applicationCode', applicationCode);
	}

	if (callbackUrl) {
		logoutUrl.searchParams.set('callbackUrl', sanitizeCallbackUrl(callbackUrl));
	}

	if (loginCallbackUrl) {
		logoutUrl.searchParams.set('loginCallbackUrl', sanitizeCallbackUrl(loginCallbackUrl));
	}

	return {
		toString: () => logoutUrl.toString(),
		set: (key: keyof LogoutUrlOptions, value: string | boolean) => {
			logoutUrl.searchParams.delete(key);
			if ((key === 'callbackUrl' || key === 'loginCallbackUrl') && typeof value === 'string') {
				logoutUrl.searchParams.set(key, sanitizeCallbackUrl(value));
				return;
			}

			logoutUrl.searchParams.set(key, value.toString());
		},
		delete: (key: keyof LogoutUrlOptions) => {
			logoutUrl.searchParams.delete(key);
		},
		navigate: async () => {
			await navigateTo(logoutUrl.toString(), { external: true });
		},
	};
}
